import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { SplineScene } from './SplineScene';
import { Aurora } from './animations/Aurora';
import { BlurText } from './animations/BlurText';
import { DecryptedText } from './animations/DecryptedText';
import { Magnet } from './animations/Magnet';
/**
 * Two-screen hero.
 *
 *   Screen 1 (first viewport): brand reveal — badge + logo, centered.
 *   Screen 2 (second viewport): headline, copy, and CTAs.
 *
 * Both share a pinned background (Aurora + 3D scene + vignette) so the
 * atmosphere stays continuous as the user scrolls between screens.
 *
 * No cross-fade math — each act gets its own dedicated 100vh of scroll.
 * Animations trigger on viewport entry, so they feel deterministic.
 */
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  // The pinned background gets a very gentle parallax shift between the
  // two screens — just enough motion to feel alive, not enough to compete.
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.8, 1], [0.45, 0.3, 0.15]);
  return <section ref={ref} className="relative w-full" data-id="element-507">
      {/* SVG colour-matrix filter — drops white in the logo image to transparent */}
      <svg width="0" height="0" className="absolute" aria-hidden="true" data-id="element-508">
        <defs data-id="element-509">
          <filter id="periscan-remove-white" colorInterpolationFilters="sRGB" data-id="element-510">
            <feColorMatrix type="matrix" values="-1 0 0 0 1
                       0 -1 0 0 1
                       0 0 -1 0 1
                       0 0 0 1 0" data-id="element-511" />
            <feColorMatrix type="hueRotate" values="180" data-id="element-512" />
            <feColorMatrix type="matrix" values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      1 1 1 0 0" data-id="element-513" />
          </filter>
        </defs>
      </svg>

      {/* ── PINNED BACKGROUND ─────────────────────────────────────────── */}
      {/* Sticky takes up no layout space (-mb-[100vh]) so both screens
                  flow over it normally while the atmosphere stays in place. */}
      <div className="sticky top-0 h-screen w-full overflow-hidden -mb-[100vh] z-0" data-id="element-514">
        <Aurora intensity={0.18} data-id="element-515" />

        <motion.div style={{
        scale: bgScale,
        opacity: bgOpacity
      }} className="absolute inset-0 pointer-events-none" data-id="element-516">
          <SplineScene data-id="element-517" />
        </motion.div>

        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" data-id="element-518" />

        {/* Stronger vignette so content always pops */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, rgba(5,6,8,0.3) 15%, rgba(5,6,8,0.75) 55%, #050608 90%)'
      }} data-id="element-519" />
      </div>

      {/* ── SCREEN 1: BADGE + LOGO ───────────────────────────────────── */}
      <div className="relative z-10 h-screen w-full flex flex-col items-center justify-center px-6" data-id="element-520">
        {/* REBORN 2026 badge */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-10" data-id="element-521">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 text-[#1a8fd4] text-xs font-mono uppercase tracking-widest backdrop-blur-sm" data-id="element-522">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1a8fd4] animate-pulse" data-id="element-523" />
            <DecryptedText text="REBORN 2026" speed={60} animateOn="mount" data-id="element-524" />
          </span>
        </motion.div>

        {/* Logo — original artwork, white background filtered to transparent */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.1,
        ease: 'easeOut'
      }} className="relative flex items-center justify-center" data-id="element-525">
          <motion.div className="absolute inset-0 bg-[#1a8fd4]/25 blur-[100px] rounded-full" animate={{
          scale: [1, 1.15, 1],
          opacity: [0.5, 0.9, 0.5]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-526" />
          <img src="https://cdn.magicpatterns.com/uploads/19wZLyvpjSe2tkaaA1WT1m/p_logo_02_(1)_(1).jpg" alt="Periscan" className="relative h-28 sm:h-32 md:h-40 lg:h-48 w-auto object-contain drop-shadow-[0_0_60px_rgba(26,143,212,0.5)]" style={{
          filter: 'url(#periscan-remove-white)'
        }} data-id="element-527" />
        </motion.div>

        {/* Scroll cue */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.4,
        duration: 0.8
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none" data-id="element-528">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50" data-id="element-529">
            Scroll to continue
          </span>
          <motion.div animate={{
          y: [0, 8, 0],
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-px h-10 bg-gradient-to-b from-[#1a8fd4] to-transparent" data-id="element-530" />
        </motion.div>
      </div>

      {/* ── SCREEN 2: HEADLINE + CTAs ─────────────────────────────────── */}
      <div className="relative z-10 h-screen w-full flex items-center px-6 lg:px-8" data-id="element-531">
        <div className="max-w-5xl mx-auto w-full text-center" data-id="element-532">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-30%'
        }} transition={{
          duration: 0.5
        }} className="mb-8 flex justify-center" data-id="element-533">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs font-mono uppercase tracking-widest" data-id="element-534">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a8fd4]" data-id="element-535" />
              Periscan · Since 2004
            </span>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] font-semibold tracking-[-0.03em] leading-[1] text-white" data-id="element-536">
            <BlurText text="Periscan is" splitBy="word" delay={0.1} data-id="element-537" />
            <br data-id="element-538" />
            <span className="text-white/40" data-id="element-539">
              <BlurText text="coming back." splitBy="word" delay={0.35} data-id="element-540" />
            </span>
          </h1>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed" data-id="element-541">
            Since 2004, Periscan pioneered continuous security validation — the
            first to autonomously and continuously walk real attack paths,
            verify what's actually exploitable, and prove risks are gone. Now
            we're reborn, AI-native, and built for the way modern teams buy
            security.
          </motion.p>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="mt-6 font-mono text-sm text-[#1a8fd4]" data-id="element-542">
            <DecryptedText text="Find the path. Fix the risk. Prove it's gone." speed={30} data-id="element-543" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.7,
          delay: 0.85
        }} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center" data-id="element-544">
            <Magnet strength={0.2} data-id="element-545">
              <a href="#early-access" className="group relative overflow-hidden inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black font-medium rounded-full text-sm hover:bg-white/90 transition-colors" data-id="element-546">
                <span className="relative z-10" data-id="element-547">Get Early Access</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 relative z-10" data-id="element-548" />
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#1a8fd4]/20 to-transparent" data-id="element-549" />
              </a>
            </Magnet>
            <Magnet strength={0.15} data-id="element-550">
              <a href="#early-access" className="group inline-flex items-center gap-3 px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-full text-sm transition-colors backdrop-blur-sm" data-id="element-551">
                <span data-id="element-552">Get Your First Attack-Path Report</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-553" />
              </a>
            </Magnet>
          </motion.div>
        </div>
      </div>
    </section>;
}